import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

const KEY = (pid: string) => ['projects', pid, 'weekly-progress'] as const;
const BURNUP_KEY = (pid: string) => ['projects', pid, 'burnup'] as const;

export function useWeeklyProgress(projectId: string | undefined) {
  return useQuery({
    queryKey: KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/weekly-progress`),
    enabled: !!projectId,
  });
}

export function useBurnup(projectId: string | undefined) {
  return useQuery({
    queryKey: BURNUP_KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/weekly-progress/burnup`),
    enabled: !!projectId,
  });
}

export function useCreateWeeklyProgress(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`/api/v1/projects/${pid}/weekly-progress`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: BURNUP_KEY(pid) });
    },
  });
}

export function useTakeSnapshot(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`/api/v1/projects/${pid}/weekly-progress/snapshot`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: BURNUP_KEY(pid) });
    },
  });
}

export function useUpdateWeeklyProgress(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`/api/v1/projects/${pid}/weekly-progress/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: BURNUP_KEY(pid) });
    },
  });
}

export function useDeleteWeeklyProgress(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/projects/${pid}/weekly-progress/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: BURNUP_KEY(pid) });
    },
  });
}
